/**
 * The link view: the co-occurrence graph, ranked, reviewed, and always opening its
 * own evidence.
 *
 * `cooccurrence-graph` design: no edge is ever a number alone. Selecting one lists
 * every mention pair behind it, grouped by document, and selecting a pair paints
 * both mentions in the shared evidence panel at once — the mechanism by which a
 * discovered relationship stays checkable rather than becoming an assertion with a
 * citation-shaped decoration.
 *
 * Two kinds, never merged into one list position: `co_document` and `co_segment` are
 * visually distinct (a border colour and a window annotation), and confirming or
 * rejecting an edge appends a verdict — nothing here ever edits a row, because there
 * is no row to edit.
 */

import { useCallback, useMemo, useState } from "react";
import {
  api,
  type DeclaredEdge,
  type EdgeDetail,
  type EdgeKind,
  type GraphEdge,
} from "../lib/api";
import { useEvidence } from "../state/evidence";
import { useGatedQuery } from "../state/renderGate";

function edgeKey(edge: Pick<GraphEdge, "kind" | "a" | "b">): string {
  return `${edge.kind}:${[edge.a, edge.b].sort().join(":")}`;
}

/** Design D5: a dated declared edge renders only in "as of {date}" form, an undated
 * one only with an explicit date-unknown marking — never in the present tense. */
function formatValidAsOf(edge: DeclaredEdge): string {
  return edge.valid_as_of ? `as of ${edge.valid_as_of}` : "date unknown";
}

export function Links({ datasetId }: { datasetId: string }) {
  const { select } = useEvidence();
  const [kindFilter, setKindFilter] = useState<EdgeKind | "">("");
  const [showRejected, setShowRejected] = useState(false);
  // identity-review-triage design D4: mirrors `showRejected` exactly — a stamped
  // edge is excluded from the default listing behind this toggle, never dropped.
  const [showSuspected, setShowSuspected] = useState(false);
  const [selectedEdge, setSelectedEdge] = useState<GraphEdge | null>(null);
  const [detail, setDetail] = useState<EdgeDetail | null>(null);
  // Detail-pane errors are their own channel, separate from the list gate's own
  // error below — the same "a caller may clear only errors it owns" rule
  // `Identities.tsx` applies (design D2), so a list refresh triggered by `decide()`
  // can never blank an error `openEdge()` just set.
  const [detailError, setDetailError] = useState<string | null>(null);

  // D1: the list's query captured with its rows, so a kind-filter or
  // show-rejected switch paints zero frames of the previous filter's edges — see
  // `state/renderGate.ts`.
  const query = useMemo(
    () => ({ datasetId, kindFilter, showRejected, showSuspected }),
    [datasetId, kindFilter, showRejected, showSuspected],
  );
  const fetchGraph = useCallback(
    (active: typeof query) =>
      api.cooccurrenceGraph(active.datasetId, {
        ...(active.kindFilter ? { kind: active.kindFilter } : {}),
        rejected: active.showRejected,
        showSuspected: active.showSuspected,
        limit: 100,
      }),
    [],
  );
  const { rows: graph, error: listError, reload: loadGraph } = useGatedQuery(
    query,
    fetchGraph,
  );
  const error = detailError ?? listError;

  const displayValue = (clusterId: string): string =>
    graph?.nodes.find((n) => n.cluster_id === clusterId)?.display_value ?? clusterId;

  async function openEdge(edge: GraphEdge) {
    setSelectedEdge(edge);
    setDetailError(null);
    try {
      setDetail(
        await api.cooccurrenceEdge(datasetId, edge.a, edge.b, edge.kind, edge.window_seconds ?? undefined),
      );
    } catch (cause) {
      setDetailError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  async function decide(edge: GraphEdge, verdict: "confirmed" | "rejected") {
    setDetailError(null);
    try {
      const result = await api.submitLinkVerdict(datasetId, {
        kind: edge.kind,
        a: edge.a,
        b: edge.b,
        verdict,
      });
      await loadGraph();
      if (selectedEdge && edgeKey(selectedEdge) === edgeKey(edge)) {
        // Refresh from the store's answer, not the pre-verdict `edge` passed in:
        // `selectedEdge` is what gates the Confirm/Reject buttons below, and an
        // edge object whose own `review_state` is stale invites a second,
        // meaningless decision on an edge already decided (design D2, the
        // Links-view variant of the same defect family as the identity pane).
        setSelectedEdge({ ...edge, review_state: result.review_state });
        try {
          setDetail(
            await api.cooccurrenceEdge(
              datasetId,
              edge.a,
              edge.b,
              edge.kind,
              edge.window_seconds ?? undefined,
            ),
          );
        } catch (cause) {
          setDetailError(cause instanceof Error ? cause.message : String(cause));
        }
      }
    } catch (cause) {
      setDetailError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  async function decideDeclared(edge: DeclaredEdge, verdict: "confirmed" | "rejected") {
    setDetailError(null);
    try {
      await api.submitRelationVerdict(datasetId, edge.relation_id, verdict);
      await loadGraph();
    } catch (cause) {
      setDetailError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  function openDeclaredEdge(edge: DeclaredEdge) {
    select({
      blockId: edge.subject.block_id,
      spans: [
        {
          start_cp: edge.subject.start_cp,
          end_cp: edge.subject.end_cp,
          quote: edge.subject.quote,
        },
      ],
      secondaryBlockId: edge.object.block_id,
      secondarySpans: [
        { start_cp: edge.object.start_cp, end_cp: edge.object.end_cp, quote: edge.object.quote },
      ],
      label: `${edge.subject.quote} — ${edge.relation_type} — ${edge.object.quote}`,
      origin: "link",
    });
  }

  function openPair(document: EdgeDetail["documents"][number]) {
    const spanA = document.spans_a[0];
    const spanB = document.spans_b[0];
    if (!spanA || !spanB || !detail) return;
    select({
      blockId: spanA.block_id,
      spans: [{ start_cp: spanA.start_cp, end_cp: spanA.end_cp, quote: spanA.quote }],
      secondaryBlockId: spanB.block_id,
      secondarySpans: [{ start_cp: spanB.start_cp, end_cp: spanB.end_cp, quote: spanB.quote }],
      label: `${displayValue(detail.a)} × ${displayValue(detail.b)}`,
      origin: "link",
    });
  }

  return (
    <div className="page">
      <h1>Links</h1>

      <div className="row">
        <select
          aria-label="Filter by edge kind"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value as EdgeKind | "")}
          data-testid="link-kind-filter"
        >
          <option value="">Both kinds</option>
          <option value="co_document">Co-document</option>
          <option value="co_segment">Co-segment</option>
        </select>
        <label>
          <input
            type="checkbox"
            checked={showRejected}
            onChange={(e) => setShowRejected(e.target.checked)}
            data-testid="link-show-rejected"
          />{" "}
          Show rejected
        </label>
        <label>
          <input
            type="checkbox"
            checked={showSuspected}
            onChange={(e) => setShowSuspected(e.target.checked)}
            data-testid="link-show-suspected"
          />{" "}
          Show suspected self-pairs
        </label>
      </div>

      {error && (
        <p data-testid="links-error" style={{ color: "var(--danger)" }}>
          {error}
        </p>
      )}

      <div className="workspace two-pane">
        <ol className="hits" data-testid="link-list">
          {graph?.edges.map((edge) => (
            <li key={edgeKey(edge)}>
              <button
                className="hit"
                data-testid="link-edge"
                aria-current={selectedEdge ? edgeKey(selectedEdge) === edgeKey(edge) : false}
                onClick={() => void openEdge(edge)}
                style={{
                  borderLeft: `3px solid ${
                    edge.kind === "co_document" ? "var(--accent, #2b4a7d)" : "#8ea173"
                  }`,
                }}
              >
                <span className="eyebrow" data-testid="link-kind">
                  {edge.kind === "co_document" ? "co-document" : "co-segment"}
                  {edge.kind === "co_segment" && edge.window_seconds !== null
                    ? ` · within ${edge.window_seconds}s`
                    : ""}
                </span>
                <span className="hit-text mono">
                  {displayValue(edge.a)} × {displayValue(edge.b)}
                </span>
                <span className="eyebrow">
                  {edge.count} {edge.kind === "co_document" ? "document(s)" : "occurrence(s)"} ·
                  statistic {edge.statistic.toFixed(2)}
                </span>
                <span
                  data-testid="link-state"
                  className={edge.review_state === "proposed" ? "gap" : "eyebrow"}
                >
                  {edge.review_state === "proposed"
                    ? "⚠ proposed — unreviewed"
                    : edge.review_state}
                </span>
                {edge.suspected_self_pair && (
                  <span data-testid="link-suspected-self-pair" className="gap">
                    ⚠ possibly one person — suspected self-pair
                  </span>
                )}
              </button>
            </li>
          ))}
        </ol>

        {graph?.edges.length === 0 && (
          <p data-testid="links-empty" style={{ color: "var(--ink-faint)" }}>
            No edges yet. Run resolution once tier-1 person extraction has produced
            full-name identities that co-occur.
          </p>
        )}

        {graph && graph.declared_edges.length > 0 && (
          <div data-testid="declared-edge-section">
            <h3>Declared edges</h3>
            <p className="eyebrow">
              Stated by a registry row, not inferred — visually distinct from
              co-occurrence below.
            </p>
            <ol className="hits" data-testid="declared-edge-list">
              {graph.declared_edges.map((edge) => (
                <li key={edge.relation_id}>
                  <div
                    className="hit"
                    data-testid="declared-edge"
                    style={{
                      borderLeft: "3px solid var(--declared, #8a4baf)",
                      cursor: "pointer",
                    }}
                    onClick={() => openDeclaredEdge(edge)}
                  >
                    <span className="eyebrow" data-testid="declared-edge-source">
                      declared · {edge.relation_type}
                    </span>
                    <span className="hit-text mono">
                      {edge.subject.quote} → {edge.object.quote}
                    </span>
                    <span
                      className="eyebrow"
                      data-testid="declared-edge-date"
                      style={!edge.valid_as_of ? { fontStyle: "italic" } : undefined}
                    >
                      {formatValidAsOf(edge)}
                    </span>
                    <span
                      data-testid="declared-edge-state"
                      className={edge.review_state === "proposed" ? "gap" : "eyebrow"}
                    >
                      {edge.review_state === "proposed"
                        ? "⚠ proposed — unreviewed"
                        : edge.review_state}
                    </span>
                    {edge.review_state === "proposed" && (
                      <span className="row" onClick={(e) => e.stopPropagation()}>
                        <button
                          data-testid="confirm-declared-edge"
                          onClick={() => void decideDeclared(edge, "confirmed")}
                        >
                          Confirm
                        </button>
                        <button
                          data-testid="reject-declared-edge"
                          onClick={() => void decideDeclared(edge, "rejected")}
                        >
                          Reject
                        </button>
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {detail && (
          <section className="pane" data-testid="link-detail">
            <h2>
              {displayValue(detail.a)} × {displayValue(detail.b)}
            </h2>
            <p className="eyebrow">
              {detail.count} document(s) · state:{" "}
              <span data-testid="link-detail-state">{detail.review_state}</span>
            </p>

            {selectedEdge && selectedEdge.review_state === "proposed" && (
              <span className="row">
                <button
                  data-testid="confirm-edge"
                  onClick={() => void decide(selectedEdge, "confirmed")}
                >
                  Confirm
                </button>
                <button
                  data-testid="reject-edge"
                  onClick={() => void decide(selectedEdge, "rejected")}
                >
                  Reject
                </button>
              </span>
            )}

            <h3>Mention pairs, by document</h3>
            <ol className="hits" data-testid="link-pair-list">
              {detail.documents.map((document) => (
                <li key={document.document_id}>
                  <button
                    className="hit"
                    data-testid="link-pair"
                    onClick={() => openPair(document)}
                  >
                    <span className="hit-text mono">
                      {document.spans_a[0]?.quote} / {document.spans_b[0]?.quote}
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
    </div>
  );
}
